import { useMemo } from 'react'
import { useSessions } from '../../api/hooks'
import { shortDate, isWorkSet, logSummary, topWeight, type ExerciseLog } from '../../lib/session'

/**
 * Bir hareketin geçmişi ve gelişimi.
 *
 * Neden ayrı bir ekran değil de yerinde açılan bir panel: bu bilgiye en çok
 * ihtiyaç duyulan an, o hareketi yaparken. "Geçen ay ne kaldırmıştım" sorusu
 * için başka bir sekmeye gidip geri dönmek, set arasındaki 90 saniyeyi yiyor.
 *
 * Grafik bilerek minik ve eksensiz: burada okunması gereken tek şey ÇİZGİNİN
 * YÖNÜ. Tam eksenli bir grafik aynı yeri üç katına çıkarır ve ek bir şey
 * söylemez — sayılar zaten altındaki listede duruyor.
 */
export function ExerciseHistory({ slug, unit }: { slug: string; unit: 'rep' | 'sec' }) {
  const sessions = useSessions()

  const rows = useMemo(() => {
    const out: { date: string; log: ExerciseLog; top: number | null; best: number }[] = []
    for (const s of sessions.data ?? []) {
      if (s.completedAt === null) continue
      for (const e of s.exercises) {
        if (e.slug !== slug || e.skipped) continue
        const work = e.sets.filter((x) => x.done && isWorkSet(x))
        if (work.length === 0) continue
        out.push({
          date: s.date,
          log: e,
          top: topWeight(e),
          best: Math.max(0, ...work.map((x) => (unit === 'sec' ? x.seconds : x.reps) ?? 0)),
        })
      }
    }
    // Sunucu tarihe göre azalan gönderiyor; grafik için eskiden yeniye lazım.
    return out.sort((a, b) => a.date.localeCompare(b.date))
  }, [sessions.data, slug, unit])

  if (sessions.isLoading) return <p className="note">Geçmiş yükleniyor…</p>
  if (rows.length === 0) {
    return (
      <p className="note">
        Bu hareketin kaydı yok. İlk antrenmanı bitirdiğinde burada ağırlık ve
        tekrar gelişimin birikmeye başlayacak.
      </p>
    )
  }

  // Yüklenebilir hareketse ağırlık, değilse tekrar/süre izleniyor: vücut
  // ağırlığı hareketinde "kaç kilo" sabit, ilerleme tekrarda oluyor.
  const loadable = rows.some((r) => (r.top ?? 0) > 0)
  const series = rows.map((r) => (loadable ? (r.top ?? 0) : r.best))
  const unitText = loadable ? 'kg' : unit === 'sec' ? 'sn' : 'tekrar'

  const best = Math.max(...series)
  const first = series[0]
  const last = series[series.length - 1]
  const delta = last - first

  return (
    <div className="exhist">
      <div className="grid g4" style={{ marginBottom: 12 }}>
        <div className="tile">
          <div className="lab">En iyi</div>
          <div className="val">{best}<span className="hero-u" style={{ fontSize: 13 }}> {unitText}</span></div>
          <div className="dl">{rows.length} antrenmanda</div>
        </div>
        <div className="tile">
          <div className="lab">Son</div>
          <div className="val">{last}<span className="hero-u" style={{ fontSize: 13 }}> {unitText}</span></div>
          <div className="dl">
            {rows.length < 2 ? 'ilk kayıt'
              : delta === 0 ? 'ilk kayıtla aynı'
              : <span style={{ color: delta > 0 ? 'var(--good-text)' : 'var(--ink-2)' }}>
                  {delta > 0 ? '+' : '−'}{Math.abs(delta)} {unitText} · ilk kayda göre
                </span>}
          </div>
        </div>
      </div>

      {series.length >= 2 && <Spark values={series} />}

      <table>
        <thead>
          <tr><th>Tarih</th><th>Setler</th><th>{loadable ? 'En ağır' : 'En iyi'}</th></tr>
        </thead>
        <tbody>
          {[...rows].reverse().slice(0, 12).map((r) => (
            <tr key={r.date}>
              <td>{shortDate(r.date)}</td>
              <td style={{ fontVariantNumeric: 'normal' }}>{logSummary(r.log)}</td>
              <td>
                {(loadable ? r.top ?? 0 : r.best)} {unitText}
                {(loadable ? r.top ?? 0 : r.best) === best && (
                  <span className="pill pill-good" style={{ marginLeft: 6 }}>en iyi</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 12 && (
        <p className="note">Son 12 antrenman gösteriliyor · toplam {rows.length} kayıt.</p>
      )}
    </div>
  )
}

/**
 * Eksensiz mini çizgi. Tek işi yön göstermek.
 * En yüksek nokta işaretli — "en iyi ne zamandı" sorusu grafikte cevaplanıyor.
 */
function Spark({ values }: { values: number[] }) {
  const W = 300, H = 56, P = 6
  const lo = Math.min(...values), hi = Math.max(...values)
  const span = hi - lo || 1
  const X = (i: number) => P + (i / (values.length - 1)) * (W - P * 2)
  const Y = (v: number) => P + (1 - (v - lo) / span) * (H - P * 2)

  const d = values.map((v, i) => `${i ? 'L' : 'M'} ${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ')
  const area = `${d} L ${X(values.length - 1).toFixed(1)} ${H - P} L ${X(0).toFixed(1)} ${H - P} Z`
  const peak = values.indexOf(hi)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="spark" role="img"
         aria-label={`Gelişim: ${values[0]} → ${values[values.length - 1]}`}
         preserveAspectRatio="none">
      <path d={area} style={{ fill: 'var(--s1-soft)' }} />
      <path d={d} style={{ stroke: 'var(--s1)', fill: 'none' }} strokeWidth={2}
            strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={X(peak)} cy={Y(hi)} r={3.5} style={{ fill: 'var(--s1)' }} />
      <circle cx={X(values.length - 1)} cy={Y(values[values.length - 1])} r={3.5}
              style={{ fill: 'var(--surface)', stroke: 'var(--s1)' }} strokeWidth={2} />
    </svg>
  )
}
